import { Paper, Switch } from '@mui/material';
import { Box } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  IContact,
  useMembershipContactsQuery,
  useSetInvoiceToContactMutation
} from '../../../generated/graphql';
import { fonts } from '../../../utils/fontStyles';
import { canUserEdit } from '../../../utils/rightUtils';
import { FormatTableData } from '../../../utils/styleUtils';
import { Colors } from '../../../utils/theme';
import PaperCard from '../../Common/Layout/PaperCard';
import DenseTable from '../../Common/Tables/DenseTable';
import { IHeadCell } from '../../Common/Tables/OverviewTable';

type IProps = {
  membershipId: number;
};

const MembershipContactsTab: React.FunctionComponent<IProps> = ({ membershipId }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const history = useNavigate();
  const { data, loading } = useMembershipContactsQuery({
    variables: { id: membershipId },
    fetchPolicy: 'no-cache'
  });
  const [setInvoiceTo, setInvoiceToResult] = useSetInvoiceToContactMutation();
  const [contacts, setContacts] = useState<IContact[]>([]);
  const canEdit = canUserEdit(state.user, Types.Membership);

  useEffect(() => {
    if (data && data.result) {
      setContacts(data?.result as IContact[]);
    }
  }, [data]);

  useEffect(() => {
    if (setInvoiceToResult.data && setInvoiceToResult.data.result) {
      const { result } = setInvoiceToResult.data;
      setContacts(contacts.map((c) => (c.id === result.id ? (result as IContact) : c)));
      const message = result.is_main
        ? `Contact ${result.fullName} set as invoice to`
        : `Contact ${result.fullName} removed from invoice to`;
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'success',
          text: message
        }
      });
    }
    if (setInvoiceToResult.error) {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: setInvoiceToResult.error.message,
          autoHide: 6000
        }
      });
    }
  }, [setInvoiceToResult]);

  const handleDetails = (rowData: IContact) => {
    dispatch({
      type: Actions.SetSelectedContact,
      payload: { name: rowData.fullName, company_name: rowData.company.name }
    });
    history(`/contacts/${rowData.id}`, { state: { ...rowData } });
  };

  const handleInvoiceTo = (rowData: IContact) => {
    setInvoiceTo({
      variables: {
        id: parseInt(rowData.id),
        is_main: !rowData.is_main
      }
    });
  };

  const renderInvoiceTo = (rowData: IContact) => {
    return (
      <Switch
        id="is-website"
        disableRipple
        name="checkeWebSite"
        disabled={!canEdit}
        onChange={() => handleInvoiceTo(rowData)}
        checked={rowData.is_main || false}
      />
    );
  };

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 66
    },
    {
      title: 'Invoice to',
      sorting: false,
      render: renderInvoiceTo,
      width: 86
    },
    {
      title: 'ID',
      width: 89,
      field: 'id',
      type: 'numeric'
    },
    {
      title: 'Name',
      field: 'fullName',
      width: 203
    },
    {
      title: 'Title General',
      field: 'titleInternal.name',
      width: 172
    },
    {
      title: 'Address',
      field: 'address.street',
      width: 265
    },
    {
      title: 'Company',
      field: 'company.name',
      width: 265
    },
    {
      title: 'Email',
      field: 'email',
      width: 270
    }
  ] as IHeadCell[];

  return (
    <Box className="centralize" sx={{ width: '100%', paddingTop: '80px' }}>
      <PaperCard headerText="Contacts" width="82%" additionalStyle={{ height: '680px' }}>
        <DenseTable
          columns={columns}
          data={FormatTableData(contacts)}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={() => true}
          pageSize={10}
          crudType={Types.Membership}
          additionalToolbar={true}
          fixedHeaders={true}
        />
      </PaperCard>
    </Box>
  );
};

export default MembershipContactsTab;
